import { useEffect, useState } from 'react';

/**
 * HomeLoader
 * ----------
 * Appears every time the homepage is mounted (no sessionStorage gate — so
 * every navigation back to "/" shows it again, as requested).
 *
 * Lifecycle:
 *   0 → 2200ms : loader visible, progress bar animates to 100%
 *   2200ms      : fadeOut class added (0.5s CSS transition)
 *   2700ms      : loader unmounts, body scroll restored
 *
 * The parent (Home.js) receives `onDone` so it can defer rendering
 * heavy sections until the loader exits.
 */

const HomeLoader = ({ onDone }) => {
  const [phase, setPhase] = useState('in'); // 'in' | 'out'

  // Lock scroll while loader is visible
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    // Start fade-out after 3500ms (allows progress bar to reach 100% and linger briefly)
    const fadeTimer = setTimeout(() => setPhase('out'), 3900);
    // Unmount + notify parent after fade completes
    const doneTimer = setTimeout(() => {
      document.body.style.overflow = '';
      if (onDone) onDone();
    }, 3900 + 500); // 3900ms for fade-out + 500ms for CSS transition
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, [onDone]);

  return (
    <>
      <style>{`
        /* ── Loader overlay ───────────────────────────────────── */
        .hld-overlay {
          position: fixed;
          inset: 0;
          z-index: 999999;
          background: #000c12;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0;
          /* fade-in immediately */
          opacity: 1;
          transition: opacity 0.5s ease;
          pointer-events: all;
        }
        .hld-overlay.hld-out {
          opacity: 0;
          pointer-events: none;
        }

        /* ── Logo mark ────────────────────────────────────────── */
        .hld-logo {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          margin-bottom: 48px;
          animation: hld-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes hld-rise {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hld-logo img {
          width: 300px;
          height: auto;
          object-fit: contain;
          filter: brightness(0) invert(1);
        }
        .hld-brand-name {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.45);
        }

        /* ── Spinner ring ─────────────────────────────────────── */
        .hld-spinner {
          position: relative;
          width: 56px;
          height: 56px;
          margin-bottom: 32px;
          animation: hld-rise 0.7s 0.15s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hld-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 2px solid transparent;
        }
        /* Outer slow ring */
        .hld-ring-outer {
          border-top-color: #00a8cc;
          border-right-color: rgba(0,168,204,0.25);
          animation: hld-spin 1.1s linear infinite;
        }
        /* Inner fast ring */
        .hld-ring-inner {
          inset: 8px;
          border-top-color: rgba(0,168,204,0.55);
          border-left-color: rgba(0,168,204,0.2);
          animation: hld-spin-rev 0.75s linear infinite;
        }
        /* Center dot */
        .hld-dot {
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #00a8cc;
          box-shadow: 0 0 10px 2px rgba(0,168,204,0.6);
          animation: hld-pulse 1.1s ease-in-out infinite;
        }
        @keyframes hld-spin     { to { transform: rotate(360deg); } }
        @keyframes hld-spin-rev { to { transform: rotate(-360deg); } }
        @keyframes hld-pulse {
          0%, 100% { opacity: 1;   transform: translate(-50%,-50%) scale(1); }
          50%       { opacity: 0.4; transform: translate(-50%,-50%) scale(0.65); }
        }

        /* ── Progress bar ─────────────────────────────────────── */
        .hld-progress-wrap {
          width: 200px;
          animation: hld-rise 0.7s 0.25s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hld-progress-track {
          height: 2px;
          background: rgba(255,255,255,0.08);
          border-radius: 2px;
          overflow: hidden;
          margin-bottom: 12px;
        }
        .hld-progress-fill {
          height: 100%;
          width: 0%;
          border-radius: 2px;
          background: linear-gradient(90deg, #1d6a88, #00a8cc, #ffffff30);
          /* Animate from 0 → 100% over 2s with a natural ease */
          animation: hld-fill 2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        @keyframes hld-fill {
          0%   { width: 0%; }
          60%  { width: 75%; }
          85%  { width: 90%; }
          100% { width: 100%; }
        }
        .hld-label {
          text-align: center;
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.28);
        }

        /* ── Corner accent lines ──────────────────────────────── */
        .hld-corner {
          position: absolute;
          width: 22px;
          height: 22px;
          opacity: 0.35;
        }
        .hld-corner::before,
        .hld-corner::after {
          content: '';
          position: absolute;
          background: #00a8cc;
        }
        .hld-corner::before { width: 100%; height: 1.5px; top: 0; left: 0; }
        .hld-corner::after  { width: 1.5px; height: 100%; top: 0; left: 0; }
        .hld-tl { top: 28px; left: 28px; }
        .hld-tr { top: 28px; right: 28px; transform: scaleX(-1); }
        .hld-bl { bottom: 28px; left: 28px; transform: scaleY(-1); }
        .hld-br { bottom: 28px; right: 28px; transform: scale(-1); }

        /* Ambient glow */
        .hld-glow {
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 420px;
          height: 420px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0,168,204,0.06) 0%, transparent 70%);
          pointer-events: none;
        }
      `}</style>

      <div className={`hld-overlay${phase === 'out' ? ' hld-out' : ''}`}>
        {/* Ambient glow */}
        <div className="hld-glow" />

        {/* Corner accents */}
        <div className="hld-corner hld-tl" />
        <div className="hld-corner hld-tr" />
        <div className="hld-corner hld-bl" />
        <div className="hld-corner hld-br" />

        {/* Logo */}
        <div className="hld-logo">
          <img src="/images/logo.png" alt="Grand Indigo Embroidery" />
        </div>

        {/* Spinner */}
        <div className="hld-spinner">
          <div className="hld-ring hld-ring-outer" />
          <div className="hld-ring hld-ring-inner" />
          <div className="hld-dot" />
        </div>

        {/* Progress */}
        <div className="hld-progress-wrap">
          <div className="hld-progress-track">
            <div className="hld-progress-fill" />
          </div>
          <div className="hld-label">Loading</div>
        </div>
      </div>
    </>
  );
};

export default HomeLoader;